/**
 * SurveyChat Component
 * Main chat-style interface for survey questions
 * 
 * Features:
 * - Restores chat history when user returns to an in-progress survey
 * - Allows editing previous answers
 * - Persists progress automatically
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { surveyAPI } from '../services/api';
import ProgressBar from './ProgressBar';

/**
 * Chat-style survey interface
 * @param {Object} props
 * @param {string} props.token - Survey session token
 * @param {Object} props.sessionData - Session information
 * @param {Function} props.onComplete - Callback when survey is done
 */
function SurveyChat({ token, sessionData, onComplete }) {
    // State
    const [messages, setMessages] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [userInput, setUserInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    
    // Edit mode state
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editText, setEditText] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    // Refs
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const editTextareaRef = useRef(null);

    const isAlreadyCompletedError = useCallback((err) => {
        const message = (err?.message || '').toLowerCase();
        return message.includes('already completed');
    }, []);

    /**
     * Scrolls chat to bottom
     */
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    /**
     * Loads the next question from the API
     */
    const loadNextQuestion = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            const response = await surveyAPI.getNextQuestion(token);
            
            if (response.is_complete) {
                // Survey is complete
                onComplete();
                return;
            }

            // Update progress
            setProgress(response.progress);

            // Set current question
            setCurrentQuestion({
                number: response.question_number,
                text: response.question_text,
                isFollowup: response.is_followup,
                parentQuestionNumber: response.parent_question_number,
            });

            // Add question to messages
            setMessages((prev) => [
                ...prev,
                {
                    id: `q-${response.question_number}-${Date.now()}`,
                    role: 'assistant',
                    text: response.question_text,
                    questionNumber: response.question_number,
                    isFollowup: response.is_followup,
                },
            ]);
        } catch (err) {
            if (isAlreadyCompletedError(err)) {
                onComplete();
                return;
            }
            setError(err.message || 'Failed to load question');
            console.error('Error loading question:', err);
        } finally {
            setIsLoading(false);
        }
    }, [token, onComplete, isAlreadyCompletedError]);

    /**
     * Handles answer submission
     */
    const handleSubmit = useCallback(async (e) => {
        e?.preventDefault();
        
        const trimmedInput = userInput.trim();
        if (!trimmedInput || !currentQuestion || isSubmitting) {
            return;
        }

        // Add user's answer to messages immediately
        const userMessage = {
            id: `a-${currentQuestion.number}-${Date.now()}`,
            role: 'user',
            text: trimmedInput,
            questionNumber: currentQuestion.number,
        };
        setMessages((prev) => [...prev, userMessage]);
        
        // Clear input and set submitting state
        setUserInput('');
        setIsSubmitting(true);
        setError(null);

        try {
            const response = await surveyAPI.submitAnswer(
                token,
                currentQuestion.number,
                trimmedInput,
                currentQuestion.text
            );

            if (response.is_complete) {
                // Survey complete
                onComplete();
                return;
            }

            // Update progress
            setProgress(response.progress);

            // Set next question
            setCurrentQuestion({
                number: response.question_number,
                text: response.question_text,
                isFollowup: response.is_followup,
                parentQuestionNumber: response.parent_question_number,
            });

            // Add next question to messages
            setMessages((prev) => [
                ...prev,
                {
                    id: `q-${response.question_number}-${Date.now()}`,
                    role: 'assistant',
                    text: response.question_text,
                    questionNumber: response.question_number,
                    isFollowup: response.is_followup,
                },
            ]);
        } catch (err) {
            if (isAlreadyCompletedError(err)) {
                onComplete();
                return;
            }
            setError(err.message || 'Failed to submit answer');
            console.error('Error submitting answer:', err);
        } finally {
            setIsSubmitting(false);
        }
    }, [userInput, currentQuestion, isSubmitting, token, onComplete, isAlreadyCompletedError]);

    /**
     * Starts editing a previous answer
     */
    const handleStartEdit = useCallback((message) => {
        setEditingMessageId(message.id);
        setEditText(message.text);
        // Focus the edit textarea after it renders
        setTimeout(() => {
            editTextareaRef.current?.focus();
        }, 50);
    }, []);

    /**
     * Cancels editing
     */
    const handleCancelEdit = useCallback(() => {
        setEditingMessageId(null);
        setEditText('');
    }, []);

    /**
     * Saves edited answer
     */
    const handleSaveEdit = useCallback(async (message) => {
        const trimmedText = editText.trim();
        if (!trimmedText || trimmedText === message.text) {
            handleCancelEdit();
            return;
        }

        setIsUpdating(true);
        setError(null);

        try {
            await surveyAPI.updateAnswer(token, message.questionNumber, trimmedText);
            
            // Update the message in state
            setMessages((prev) => 
                prev.map((m) => 
                    m.id === message.id 
                        ? { ...m, text: trimmedText, edited: true }
                        : m
                )
            );
            
            handleCancelEdit();
        } catch (err) {
            setError(err.message || 'Failed to update answer');
            console.error('Error updating answer:', err);
        } finally {
            setIsUpdating(false);
        }
    }, [editText, token, handleCancelEdit]);

    /**
     * Handles keyboard shortcuts
     */
    const handleKeyDown = useCallback((e) => {
        // Submit on Ctrl+Enter or Cmd+Enter
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            handleSubmit(e);
        }
    }, [handleSubmit]);

    /**
     * Handles keyboard shortcuts in edit mode
     */
    const handleEditKeyDown = useCallback((e, message) => {
        if (e.key === 'Escape') {
            handleCancelEdit();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            handleSaveEdit(message);
        }
    }, [handleCancelEdit, handleSaveEdit]);

    /**
     * Auto-resize textarea based on content
     */
    const handleInputChange = (e) => {
        setUserInput(e.target.value);
        
        // Auto-resize
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
        }
    };

    // Load chat history and current question on mount
    useEffect(() => {
        let isMounted = true;
        
        const initializeChat = async () => {
            try {
                setIsLoading(true);
                setError(null);
                
                // Fetch personalized welcome message from AI
                const welcomeData = await surveyAPI.getWelcomeMessage(token);
                
                if (!isMounted) return;
                
                // Use AI-generated welcome or fallback to Gus default
                const welcomeText = welcomeData.message || 
                    `Hi ${sessionData?.employee_name || 'there'}! I'm Gus, and I'll be your interviewer today. I'd love to hear about your role and understand how you really spend your time—the day-to-day reality, not just what's on paper. Think of this as an informal conversation where your honest insights help us better support you. Ready to dive in?`;
                
                // Initialize messages array with welcome
                const initialMessages = [
                    {
                        id: 'welcome',
                        role: 'assistant',
                        text: welcomeText,
                        isWelcome: true,
                        isGus: true,
                    },
                ];
                
                // Check if there are existing responses (resuming survey)
                let existingResponses = [];
                try {
                    existingResponses = await surveyAPI.getSurveyResponses(token);
                } catch (err) {
                    console.log('No existing responses or error fetching:', err);
                }
                
                if (!isMounted) return;
                
                // If we have previous responses, rebuild the chat history
                if (existingResponses && existingResponses.length > 0) {
                    console.log('Restoring chat history with', existingResponses.length, 'responses');
                    
                    // Sort responses by question number to maintain order
                    existingResponses.sort((a, b) => a.question_number - b.question_number);
                    
                    // Add each Q&A pair to messages
                    existingResponses.forEach((resp) => {
                        // Add the question
                        initialMessages.push({
                            id: `q-${resp.question_number}-restored`,
                            role: 'assistant',
                            text: resp.question_text,
                            questionNumber: resp.question_number,
                            isFollowup: resp.is_followup,
                        });
                        
                        // Add the answer
                        initialMessages.push({
                            id: `a-${resp.question_number}-restored`,
                            role: 'user',
                            text: resp.answer_text,
                            questionNumber: resp.question_number,
                        });
                    });
                }
                
                setMessages(initialMessages);
                
                // Load next question (this gets the current unanswered question)
                const response = await surveyAPI.getNextQuestion(token);
                
                if (!isMounted) return;
                
                if (response.is_complete) {
                    onComplete();
                    return;
                }

                // Update progress
                setProgress(response.progress);

                // Set current question
                setCurrentQuestion({
                    number: response.question_number,
                    text: response.question_text,
                    isFollowup: response.is_followup,
                    parentQuestionNumber: response.parent_question_number,
                });

                // Add question to messages if not already there
                setMessages((prev) => {
                    const alreadyExists = prev.some(
                        (m) => m.questionNumber === response.question_number && m.role === 'assistant'
                    );
                    if (alreadyExists) return prev;
                    
                    return [
                        ...prev,
                        {
                            id: `q-${response.question_number}-${Date.now()}`,
                            role: 'assistant',
                            text: response.question_text,
                            questionNumber: response.question_number,
                            isFollowup: response.is_followup,
                        },
                    ];
                });
            } catch (err) {
                if (isMounted) {
                    if (isAlreadyCompletedError(err)) {
                        onComplete();
                        return;
                    }
                    setError(err.message || 'Failed to load question');
                    console.error('Error loading question:', err);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        
        initializeChat();
        
        return () => {
            isMounted = false;
        };
    }, [token, sessionData?.employee_name, onComplete, isAlreadyCompletedError]);

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // Focus textarea when not loading
    useEffect(() => {
        if (!isLoading && !isSubmitting && textareaRef.current && !editingMessageId) {
            textareaRef.current.focus();
        }
    }, [isLoading, isSubmitting, currentQuestion, editingMessageId]);

    /**
     * Formats question number for display
     */
    const formatQuestionNumber = (number, isFollowup) => {
        if (isFollowup) {
            const parentNum = Math.floor(number / 100);
            const followupNum = number % 100;
            return `${parentNum}.${followupNum}`;
        }
        return number;
    };

    /**
     * Check if a message can be edited (user messages only, survey not complete)
     */
    const canEditMessage = (message) => {
        return message.role === 'user' && sessionData?.status !== 'completed';
    };

    return (
        <div className="survey-chat">
            {/* Progress Bar */}
            {progress && <ProgressBar progress={progress} />}

            {/* Chat Messages */}
            <div className="chat-messages">
                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={`message ${message.role} ${message.isFollowup ? 'followup' : ''} ${message.isWelcome ? 'welcome' : ''} ${editingMessageId === message.id ? 'editing' : ''}`}
                    >
                        {message.role === 'assistant' && (
                            <div className="gus-avatar" title="Gus - Your AI Interviewer">
                                <span className="gus-icon">🍦</span>
                            </div>
                        )}
                        <div className="message-bubble">
                            {message.role === 'assistant' && message.isWelcome && (
                                <div className="gus-intro">
                                    <span className="gus-name">Gus</span>
                                    <span className="gus-subtitle">Your AI Interviewer</span>
                                </div>
                            )}
                            {message.role === 'assistant' && !message.isWelcome && (
                                <div className="message-meta">
                                    <span className="question-badge">
                                        {message.isFollowup ? '↳ Follow-up' : `Question ${formatQuestionNumber(message.questionNumber, message.isFollowup)}`}
                                    </span>
                                </div>
                            )}
                            
                            {/* Edit mode or display mode */}
                            {editingMessageId === message.id ? (
                                <div className="edit-container">
                                    <textarea
                                        ref={editTextareaRef}
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        onKeyDown={(e) => handleEditKeyDown(e, message)}
                                        className="edit-textarea"
                                        rows={4}
                                        disabled={isUpdating}
                                    />
                                    <div className="edit-actions">
                                        <button 
                                            onClick={handleCancelEdit}
                                            className="edit-cancel-btn"
                                            disabled={isUpdating}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            onClick={() => handleSaveEdit(message)}
                                            className="edit-save-btn"
                                            disabled={isUpdating || !editText.trim()}
                                        >
                                            {isUpdating ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                    <div className="edit-hint">
                                        Press <kbd>Esc</kbd> to cancel, <kbd>Ctrl</kbd>+<kbd>Enter</kbd> to save
                                    </div>
                                </div>
                            ) : (
                                <div className="message-content">
                                    <p>{message.text}</p>
                                    {message.edited && (
                                        <span className="edited-indicator">(edited)</span>
                                    )}
                                </div>
                            )}
                            
                            {/* Edit button for user messages */}
                            {canEditMessage(message) && editingMessageId !== message.id && (
                                <button 
                                    className="edit-button"
                                    onClick={() => handleStartEdit(message)}
                                    title="Edit this answer"
                                >
                                    ✏️ Edit
                                </button>
                            )}
                        </div>
                    </div>
                ))}

                {/* Loading indicator */}
                {(isLoading || isSubmitting) && (
                    <div className="message assistant loading">
                        <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                )}

                {/* Error display */}
                {error && (
                    <div className="message error">
                        <div className="message-content">
                            <p>⚠️ {error}</p>
                            <button 
                                onClick={() => loadNextQuestion()}
                                className="retry-button"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="chat-input-form">
                <div className="input-container">
                    <textarea
                        ref={textareaRef}
                        value={userInput}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder={
                            isSubmitting
                                ? 'Sending...'
                                : currentQuestion?.isFollowup
                                    ? 'Tell me more...'
                                    : 'Share your thoughts... (Ctrl+Enter to submit)'
                        }
                        disabled={isSubmitting || isLoading}
                        rows={3}
                        className="chat-textarea"
                    />
                    <button
                        type="submit"
                        disabled={!userInput.trim() || isSubmitting || isLoading}
                        className="submit-button"
                    >
                        {isSubmitting ? (
                            <span className="button-spinner"></span>
                        ) : (
                            <span className="send-icon">↑</span>
                        )}
                    </button>
                </div>
                <div className="input-hint">
                    <span>Press <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to submit</span>
                    {progress && (
                        <span className="progress-hint">
                            {progress.base_completed} of {progress.total_base} questions completed
                        </span>
                    )}
                </div>
            </form>
        </div>
    );
}

export default SurveyChat;
